import { createTheme } from '@mui/material/styles';

// Design tokens.
//
// Previously the palette accumulated as literals across the pages: ~14 distinct hex
// values, two different greens for a win (#28a745 and #4caf50) and two reds for a
// loss, and `primary` left at MUI's stock blue (#1976d2) while the real brand navy
// was hand-applied 22 times. Anything MUI defaulted to `primary` — the loading
// spinner, the Fab, focus rings, selected menu items — therefore rendered in a blue
// that appeared nowhere else in the design.
export const tokens = {
  navy: '#0D203D',
  navyHover: '#1D3557',
  navyTint: '#EDF1F7',

  ink: '#212529',
  inkMuted: '#6C757D',
  inkFaint: '#9AA4B2',

  ground: '#F5F7FA',
  surface: '#FFFFFF',
  rule: '#E4E9F0',
  ruleSoft: '#EFF2F6',

  // Semantic — one value each, used for both standings and result markers.
  win: '#1E7A43',
  loss: '#C0322B',
  draw: '#6C757D',

  // Competition accent, deliberately distinct from the navy structure.
  flex: '#B4531F',
};

// Type scale. Steps are ~1.08–1.18 apart: tight enough for a dense data UI, and the
// bottom step sits at 12px so nothing meaningful renders below the mobile floor.
// Max width of the readable content column. Full-bleed fixture rows at 1400px put
// the two team names at opposite edges with the score marooned in the middle.
export const CONTENT_MAX_WIDTH = 1080;

export const type = {
  label:    '0.75rem',    // 12px — uppercase labels, table headers
  caption:  '0.8125rem',  // 13px — metadata lines
  bodySm:   '0.875rem',   // 14px — dense body, table cells
  body:     '0.9375rem',  // 15px — default body, team names
  lead:     '1.0625rem',  // 17px — card titles, scores
  title:    '1.25rem',    // 20px — section headings
  display:  '1.5rem',     // 24px — page titles
};

const theme = createTheme({
  palette: {
    primary: { main: tokens.navy, dark: '#081729', light: tokens.navyHover, contrastText: '#FFFFFF' },
    secondary: { main: tokens.inkMuted },
    success: { main: tokens.win },
    error: { main: tokens.loss },
    background: { default: tokens.ground, paper: tokens.surface },
    text: { primary: tokens.ink, secondary: tokens.inkMuted },
    divider: tokens.rule,
  },

  // The app's established look is square-cornered; make that the default rather
  // than overriding borderRadius to 0 at ~30 call sites.
  shape: { borderRadius: 0 },

  typography: {
    fontFamily: [
      '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Roboto',
      '"Helvetica Neue"', 'Arial', 'sans-serif',
    ].join(','),
    button: { textTransform: 'none', fontWeight: 600, fontSize: type.bodySm },
    h4: { fontSize: type.display, fontWeight: 700, letterSpacing: '-0.01em' },
    h5: { fontSize: type.title, fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontSize: type.lead, fontWeight: 700 },
    subtitle1: { fontSize: type.body, fontWeight: 600 },
    subtitle2: { fontSize: type.bodySm, fontWeight: 600 },
    body1: { fontSize: type.body },
    body2: { fontSize: type.bodySm },
    caption: { fontSize: type.caption },
    overline: {
      fontSize: type.label, fontWeight: 700, letterSpacing: '0.08em',
      textTransform: 'uppercase', lineHeight: 1.6,
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Keep tabular figures aligned wherever numbers stack in columns.
        'table, .tabular': { fontVariantNumeric: 'tabular-nums' },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { border: `1px solid ${tokens.rule}`, boxShadow: '0 1px 3px rgba(13,32,61,.06)' },
      },
    },
    MuiPaper: { defaultProps: { elevation: 0 } },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: { root: { minHeight: 44 } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: { borderColor: tokens.rule },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${tokens.ruleSoft}` },
      },
    },
  },
});

export default theme;
